import React, { useRef, useEffect, useState } from "react";
import Table from 'components/commercetools-ui/atoms/table';
import Typography from 'components/commercetools-ui/atoms/typography';
import Button from 'components/commercetools-ui/atoms/button';
import Popup from 'components/commercetools-ui/atoms/popup';
import { tablet, mobile } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useProjectBuilder } from 'frontastic';
import Spinner from 'components/commercetools-ui/organisms/spinner';
import { FaCheck } from "react-icons/fa";
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Icon from 'components/icons/x-mark';
import SaveConfirmationPopup from "./save-confirmation-popup";
interface Variant {
    id: string;
    sku: string;
    images: string[];
    attributes: Record<string, any>;
    price: {
        fractionDigits: number;
        centAmount: number;
        currencyCode: string;
    };
}
interface Item {
    id: string;
    type: string;
    productId: string;
    name: string;
    quantity: number;
    roomName: string;
    roomNotes: string;
    variant: Variant;
    isProductAvailable?: boolean;
}
interface Section {
    roomName: string;
    items: Item[];
    showPricesOnPdf: boolean;
}
interface ProjectFormData {
    sections: Section[];
}
interface ProjectFormDataProps {
    projectForm: ProjectFormData,
    handleTabClick: (tabIndex: number, formData: any) => void;
}
interface ConfirmationPopupProps {
    isOpen: boolean;
    selectedRoom: any;
    identifier: string,
}

interface ButtonProps {
    item: object,
    sectionIndex: number,
    identifier: string;
}
interface ItemQuantityProps {
    item: any,
    sectionIndex: number,
}
interface PriceDetailsProps {
    item: any,
}
interface Payload {
    lineITemsToUpdate?: any[];
    lineItemsToRemove?: any[];
    roomsAfterRemoval?: any[];
    version: number,
    projectId: string,
    roomGroups?: any[]
    allRoomsAreDeleted?: boolean
}
const ViewProducts: React.FC<any> = ({ projectForm, handleTabClick, handleConfirmLeave, handleCancelLeave, setIsFormDirty, openSaveConfirmation, setOpenSaveConfirmation, tabIndexValue, setTabIndexValue }: any) => {
    const projectFormData = projectForm || projectForm?.data;
    const { performMutationOnMyProjectById, updateProjectStatus } = useProjectBuilder();
    const [sections, setSections] = useState<Section[]>(projectFormData.roomGroups);
    const [selectedItem, setSelectedItem] = useState({});
    const [identifier, setIdentifier] = useState("");
    const [sectionIndexValue, setSectionIndexValue] = useState(0);
    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [isTablet] = useMediaQuery(tablet);
    const [lineItemsToRemove, setLineItemsToRemove] = useState<string[]>([]);
    const [roomsAfterRemoval, setRoomsAfterRemoval] = useState<string[]>([]);
    const [roomsRemovalLineItem, setRoomsRemovalLineItem] = useState<string[]>([]);
    const [formData, setFormData] = useState(null);
    const [navigationToDiffStepperid, setNavigationToDiffStepperid] = useState(0);
    const [discardClicked, setDiscardClicked] = useState(false);
    const [localFormNavigation, setLocalFormNavigation] = useState(false);
    const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
        { id: string; quantity: number; }[]
    >([]);
    const [loading, setLoading] = useState<any>(false);
    const isFormDirtyRef = useRef(false);
    const handleButtonClick = () => {
        window.location.href = '/';
    };
    if (!projectFormData || !projectFormData?.roomGroups?.length) {
        return (
            <div className="">
                <div className='mb-20'> Browse the website to start adding products to a project. </div>
                <div className="w-[240px] h-[288px] border-2 border-dotted border-gray-400 flex items-center justify-center">
                    <button
                        onClick={handleButtonClick}
                        className="border px-18 py-[4.5px] fontSize14-sm bg-orange-100 rounded-full items-center justify-center"
                    >
                        Add products
                    </button>
                </div>
            </div>
        );
    }
    const addOrUpdateItemInLineItemsToUpdate = (id: string, quantity: number) => {
        setLineItemsToUpdate((prevItems) => {
            const existingIndex = prevItems.findIndex((item) => item.id === id);
            if (existingIndex !== -1) {
                const updatedItems = [...prevItems];
                updatedItems[existingIndex].quantity = quantity;
                return updatedItems;
            } else {
                return [...prevItems, { id, quantity }];
            }
        });
    };

    const handleDelete = (sectionIndex: number, itemId: string) => {
        setIsFormDirty(true);
        isFormDirtyRef.current = true;
        setSections((prevSections) => {
            const updatedSections = prevSections
                .map((section, idx) => {
                    if (idx === sectionIndex) {
                        const updatedItems = section.items.filter((item) => item.id !== itemId);
                        if (updatedItems.length === 0) {
                            setRoomsRemovalLineItem((prevItems) => {
                                return [...prevItems, section.roomName];
                            });
                            return null;
                        }
                        return { ...section, items: updatedItems };
                    }
                    return section;
                })
                .filter((section): section is Section => section !== null);
            return updatedSections;
        });

        setLineItemsToRemove((prevItems) => {
            if (!prevItems.includes(itemId)) {
                return [...prevItems, itemId];
            }
            return prevItems;
        });
        setOpenPopup(false);
    };

    const handleDeleteSection = (sectionIndex: number, selectedRoom: any) => {
        setIsFormDirty(true);
        isFormDirtyRef.current = true;
        setSections((prevSections) => prevSections.filter((_, idx) => idx !== sectionIndex));
        setRoomsAfterRemoval((prevItems) => {
            return [...prevItems, selectedRoom.name];
        });

        setOpenPopup(false);
    };
    const handleQuantityChange = (
        sectionIndex: number,
        id: string,
        action: "increase" | "decrease"
    ) => {
        setIsFormDirty(true);
        isFormDirtyRef.current = true;
        setSections((prevSections) =>
            prevSections.map((section, idx) =>
                idx === sectionIndex
                    ? {
                        ...section,
                        items: section.items.map((item) => {
                            if (item.id === id) {
                                const newQuantity =
                                    action === "increase" ? item.quantity + 1 : item.quantity - 1;
                                if (newQuantity > 0) {
                                    addOrUpdateItemInLineItemsToUpdate(id, newQuantity);
                                    return { ...item, quantity: newQuantity };
                                }
                            }
                            return item;
                        }),
                    }
                    : section
            )
        );
    };

    const getExcludedRoomIds = (
        parent: any,
        deletedRooms: string[]
    ): string[] => {
        const excludedRooms = deletedRooms.length === 0
            ? parent
            : parent.filter((room: any) => deletedRooms.includes(room.roomName));
        const excludedIds = excludedRooms.flatMap((room: any) =>
            room.items.map((item: any) => item.id)
        );
        return excludedIds;
    };

    const capitalizeFirstLetter = (str: string) => str?.charAt(0)?.toUpperCase() + str?.slice(1);
    const ctDataToFormData = (ctData: any) => {
        const { projectLocation, projectType, status, roomLabels } = ctData;
        const roomDetailsObj: any = {},
            selectedRoomDetails: any = [];
        roomLabels.forEach((e: any) => {
            const roomCount = parseInt(e.at(-1));
            const roomName = isNaN(roomCount) ? e.trim() : e.slice(0, e.length - 1).trim();
            if (isNaN(roomCount)) {
                roomDetailsObj[roomName] = 1;
            } else if (!roomDetailsObj[roomName] || roomCount > roomDetailsObj[roomName]) {
                roomDetailsObj[roomName] = roomCount;
            }
        });

        Object.keys(roomDetailsObj).map((e) => {
            selectedRoomDetails.push({
                roomName: e,
                count: roomDetailsObj[e],
            });
        });
        return {
            ...ctData,
            projectLocation: {
                name: projectLocation,
                value: projectLocation,
            },
            projectType: {
                name: capitalizeFirstLetter(projectType),
                value: projectType,
            },
            projectStatus: {
                name: capitalizeFirstLetter(status),
                value: status,
            },
            selectedRoomDetails,
        };
    };
    const handleSubmit = async (navId = 0) => {
        setIsFormDirty(false);
        setLocalFormNavigation(false);
        setOpenSaveConfirmation(false);
        const payload: Payload = {
            version: projectFormData.version,
            projectId: projectFormData.shoppingListId
        };
        const quantyUpdate: any[] | undefined = [];
        sections.forEach((item) => {
            item.items.forEach((value) => {
                quantyUpdate.push({ lineItemId: value.id, quantity: value.quantity });
                if (lineItemsToRemove.includes(value.id)) {
                    setLineItemsToRemove((prevItems) =>
                        prevItems.filter((id) => id !== value.id)
                    );
                }
            });
        });
        payload.lineITemsToUpdate = quantyUpdate;
        if (lineItemsToRemove.length > 0) {
            payload.lineItemsToRemove = lineItemsToRemove;
        }

        if (projectFormData?.roomGroups?.length !== sections.length) {
            const deletedRooms = [...new Set([...roomsAfterRemoval, ...roomsRemovalLineItem])];
            if (deletedRooms.length > 0) {
                payload.roomsAfterRemoval = projectFormData?.roomLabels?.filter((item: any) => !deletedRooms.includes(item));
                const excludedIds = getExcludedRoomIds(projectFormData?.roomGroups, deletedRooms);
                const concatIds = [...new Set([...lineItemsToRemove, ...excludedIds])];
                payload.lineItemsToRemove = concatIds;
            }
            if (deletedRooms?.length > 0 && payload?.roomsAfterRemoval?.length === 0) {
                payload.allRoomsAreDeleted = true;
            }
        }
        try {
            const result: any = await performMutationOnMyProjectById(payload);
            if (result?.shoppingList) {
                const newFormData = ctDataToFormData(result.shoppingList);
                setFormData(newFormData);
                handleConfirmLeave();
                if (navId !== 0) {
                    setNavigationToDiffStepperid(navId)
                } else {
                    setNavigationToDiffStepperid(tabIndexValue)
                }
                setDiscardClicked(true);
            }
        } catch (error) {
            console.log('-error-:', error);
            handleCancelLeave();
            setIsFormDirty(true);
        }
    };

    const navigationToDiffStepper = (id: number) => {
        setNavigationToDiffStepperid(id);
        setTabIndexValue(id)
        if (isFormDirtyRef.current) {
            setLocalFormNavigation(true);
            setOpenSaveConfirmation(true);
            return;
        }
        console.log("Form is clean, proceeding...");
        setTimeout(() => {
            setFormData(projectFormData);
            setOpenSaveConfirmation(false);
            setDiscardClicked(true);
        }, 0);
    };
    if (loading) {
        return <Spinner />;
    }

    const openPopupDialog = (item: any, identifier: string, index: number) => {
        setOpenPopup(true);
        setSelectedItem(item);
        setIdentifier(identifier);
        setSectionIndexValue(index);
    };
    const handlePopupClose = () => {
        setOpenPopup(false);
    };
    useEffect(() => {

        if (discardClicked && !localFormNavigation) {
            if (formData) {
                setIsFormDirty(false);
                handleTabClick(navigationToDiffStepperid, formData);
            } else {
                handleTabClick(tabIndexValue, projectFormData);
            }
        }
    }, [discardClicked, formData, localFormNavigation]);

    const discardConfirmation = () => {
        setIsFormDirty(false);
        setLocalFormNavigation(false);
        setDiscardClicked(true);
        setOpenSaveConfirmation(false);
        setTimeout(() => {
            handleConfirmLeave();
        }, 100);
    };


    const closeConfirmation = () => {
        setTabIndexValue(3);
        setOpenSaveConfirmation(false);
        handleCancelLeave();
    };

    const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({ isOpen, selectedRoom, identifier }) => {
        if (!isOpen) return null;
        return (
            <Popup isOpen={isOpen} bodyClassNames="sm:mx-unset max-w-[380px]">
                <div className="break-words p-[25px]">
                    <p className="mt-[27px] whitespace-normal text-gray-700">
                        Are you sure you want to delete <br /> <strong>{selectedRoom?.name}</strong>
                    </p>
                    <div className="mt-[51px] flex gap-31 space-x-4">
                        <Button
                            variant={'secondary'}
                            className=" cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-medium"
                            type="button"
                            onClick={() => identifier == 'parent' ? handleDeleteSection(sectionIndexValue, selectedRoom) : handleDelete(sectionIndexValue, selectedRoom.id)}
                        >
                            Yes, please delete
                        </Button>

                        <Button
                            variant={'secondary'}
                            className=" cursor-pointer !border-[#dee2e6] !px-[27px] !py-[9px] text-sm font-medium"
                            type="button"
                            onClick={handlePopupClose}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </Popup>
        );
    };
    const OpenDialogButton: React.FC<ButtonProps> = ({ item, sectionIndex, identifier }) => {
        return (
            <Button
                onClick={() => openPopupDialog(item, identifier, sectionIndex)}
                variant="ghost"
                className="!p-0"
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-20 h-20 text-red-600 cursor-pointer hover:text-red-800">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6
                                                    4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 
                                                    1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                </svg>
            </Button>
        );
    };
    const ItemQuantity: React.FC<ItemQuantityProps> = ({ item, sectionIndex }) => {
        return (
            <>
                {item.quantity > 1 && (
                    <Button
                        variant='ghost'
                        className={`!p-0 text-25 ${!item.isProductAvailable ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                            }`}
                        onClick={() => handleQuantityChange(sectionIndex, item.id, "decrease")}
                        disabled={item.quantity <= 1}
                    >
                        -
                    </Button>
                )}
                <Typography className="text-16 p-[18px]">{`${item.quantity}`}</Typography>
                <Button
                    variant='ghost'
                    className={`!p-0 text-16 ${!item.isProductAvailable ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                        }`}
                    onClick={() => handleQuantityChange(sectionIndex, item.id, "increase")}
                >
                    +
                </Button>
            </>
        );
    };
    const PriceDetails: React.FC<PriceDetailsProps> = ({ item }) => {
        return (
            <div>
                <Typography className="text-18 whitespace-normal">
                    {`$${((item.variant.price.centAmount * item.quantity) / 100).toFixed(item.variant.price.fractionDigits)}`}
                </Typography>

                <Typography className="text-16 text-[#999999] whitespace-normal">
                    (inc. GST)
                </Typography>
            </div>
        );
    };
    const [checked, setChecked] = useState(projectFormData?.showPricesOnPdf || false);
    const handleToggle = async () => {
        try {
            setChecked(!checked);
            const payload = {
                shoppingListId: projectFormData?.shoppingListId,
                version: projectFormData?.version,
                status: !checked,
                fieldName: 'showPricesOnPdf'
            };
            await updateProjectStatus(payload);
        } catch (error) {
            console.log('error:-', error);
        }
    };
    return (
        <div className="p-6">
            <div className="flex justify-between md:justify-end">
                <span>Display prices on pdf</span>
                <label className="ml-[10px] relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={handleToggle}
                        className="sr-only"
                    />
                    <div className={`w-44 h-24 rounded-full transition ${checked ? 'bg-[#2B2C39]' : 'bg-gray-300'}`}>
                        <div className={`absolute left-2 top-[4px] w-20 h-20 bg-white rounded-full flex items-center 
                            justify-center transition-transform ${checked ? 'translate-x-20' : ''}`}>
                            {checked && <FaCheck className="text-gray-500 text-3xl h-[10px]" />}
                        </div>
                    </div>
                </label>
            </div>
            {sections.map((section, sectionIndex) => (
                <div key={section.roomName} className="mb-12 overflow-x-auto">
                    <div className="flex justify-between items-center mb-[27px] mr-[10px]">
                        <Typography className="max-w-[245px] text-40 sm:max-w-[unset]">
                            {section.roomName}
                        </Typography>

                        <OpenDialogButton
                            item={{ name: section.roomName }}
                            sectionIndex={sectionIndex}
                            identifier='parent'
                        />
                    </div>
                    <div>
                        {isTablet && (
                            <Table.Container className="w-full max-w-full table-fixed border-t-0">
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Cell className="w-[10%]">
                                            <Typography className="text-16">Product Name</Typography>
                                        </Table.Cell>
                                        <Table.Cell className="w-[40%]">
                                        </Table.Cell>
                                        <Table.Cell className="w-[15%]">
                                            <Typography className="text-16">Qty</Typography>
                                        </Table.Cell>
                                        <Table.Cell className="w-[15%]">
                                            <Typography className="text-16">Code</Typography>
                                        </Table.Cell>
                                        <Table.Cell className="w-[20%]">
                                            <Typography className="text-16">Price</Typography>
                                        </Table.Cell>
                                    </Table.Row>
                                </Table.Head>
                                <Table.Body>
                                    {section.items.map((item, index) => (
                                        <>
                                            {!item.isProductAvailable && (
                                                <Table.Row className={`${index % 2 === 1 ? 'bg-gray-100' : ''} w-full shadow-none`}>
                                                    <td colSpan={5} className="p-4">
                                                        <div className="flex items-center justify-between w-full">
                                                            <div className="flex items-center space-x-2">
                                                                <InformationCircleIcon className="h-24 w-24 text-[#F27320] stroke-[#F27320]" />
                                                                <Typography className="text-[16px] text-[#F27320] ml-[10px]">
                                                                    This product you have selected may no longer be available.
                                                                </Typography>
                                                            </div>
                                                            <Button onClick={() => handleButtonClick()} className="!bg-[#A6C9CC] text-black">View recommended alternatives</Button>
                                                        </div>
                                                    </td>
                                                </Table.Row>
                                            )}
                                            <Table.Row key={item.id} className={`${index % 2 === 1 ? 'bg-gray-200' : ''} ${!item.isProductAvailable ? 'opacity-50' : ''} mb-18px w-full`}>
                                                <Table.Cell className="!p-0 !pb-[4.5px] w-[10%] overflow-hidden text-black">
                                                    <img
                                                        src={item.variant.images[0] ?? '/images/no-image-placeholder/product-thumbnail.jpg'}
                                                        alt={item.name}
                                                        className="w-80 h-80 object-cover"
                                                    />
                                                </Table.Cell>

                                                <Table.Cell className="p-1 w-[40%] overflow-hidden text-black">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <Typography className="text-16 whitespace-normal break-words font-medium">{item.name}</Typography>
                                                            <Typography className="text-14 whitespace-normal break-words">{`Colour: ${item.variant.attributes.colour}`}</Typography>

                                                        </div>
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell className="w-[15%] break-words text-black">
                                                    <div className="flex items-center space-x-2">
                                                        <ItemQuantity item={item} sectionIndex={sectionIndex} />
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell className="w-[15%] break-words text-black">
                                                    <Typography className="text-16 whitespace-normal">{item.variant.attributes["item-code"]}</Typography>
                                                </Table.Cell>
                                                <Table.Cell className="w-[20%] break-words text-black">
                                                    <div className="flex items-center justify-between">
                                                        <PriceDetails item={item} />
                                                        <OpenDialogButton
                                                            item={item}
                                                            sectionIndex={sectionIndex}
                                                            identifier='lineItem'
                                                        />
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        </>
                                    ))}
                                </Table.Body>
                            </Table.Container>
                        )}

                        {!isTablet && (
                            <>
                                {section.items.map((item, index) => (
                                    <>
                                        {!item.isProductAvailable && (
                                            <div className={`${index % 2 === 1 ? 'bg-gray-100' : ''} w-full shadow-none`}>
                                                <div>
                                                    <div className="grid w-full p-[9px]">
                                                        <div className="items-start flex space-x-2 mb-[12px]">
                                                            <InformationCircleIcon className="flex-shrink-0 h-24 w-24  max-h-64 max-w-64 text-[#F27320] stroke-[#F27320]" />
                                                            <Typography className="text-[16px] text-[#F27320] ml-[10px]">
                                                                This product you have selected may no longer be available.
                                                            </Typography>
                                                        </div>
                                                        <Button onClick={() => handleButtonClick()} className="!bg-[#A6C9CC] text-black">View recommended alternatives</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className={`${index % 2 === 1 ? 'bg-gray-200' : ''}  ${!item.isProductAvailable ? 'opacity-50' : ''} mb-18px w-full`}>
                                            <div className="p-1">
                                                <div className="flex items-start">
                                                    <img
                                                        src={item.variant.images[0] ?? '/images/no-image-placeholder/product-thumbnail.jpg'}
                                                        alt={item.name}
                                                        className="w-80 h-80 object-cover"
                                                    />
                                                    <div className="ml-16 flex w-full justify-between">
                                                        <Typography className="text-16 font-medium">{item.name}</Typography>
                                                        <div className="flex-shrink-0">
                                                            <OpenDialogButton
                                                                item={item}
                                                                sectionIndex={sectionIndex}
                                                                identifier='lineItem'
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="ml-[96px] flex items-center justify-between">
                                                    <Typography className="text-12 whitespace-normal break-words">{`Colour: ${item.variant.attributes.colour}`}</Typography>
                                                    <Typography className="text-12 whitespace-normal break-words">{item.variant.attributes["item-code"]}</Typography>
                                                </div>

                                                <div className="ml-[96px] flex items-center justify-between">
                                                    <PriceDetails item={item} />
                                                    <div className="flex items-center space-x-2">
                                                        <ItemQuantity item={item} sectionIndex={sectionIndex} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            ))}
            <div className="popup-container">
                <ConfirmationPopup
                    isOpen={openPopup}
                    selectedRoom={selectedItem}
                    identifier={identifier}
                />
            </div>
            <div className="savepopup-container">
                <SaveConfirmationPopup
                    isOpen={openSaveConfirmation}
                    handleDiscard={discardConfirmation}
                    handleSaveNContinue={() => handleSubmit()}
                    handleClose={closeConfirmation}
                />
            </div>
            <div className="flex flex-col md:flex-row justify-between">
                <div className="mb-4 md:!mb-0">
                    {isTablet && (
                        <Button onClick={() => navigationToDiffStepper(2)}>
                            Back to products
                        </Button>
                    )}
                    {!isTablet && (
                        <Button className="w-full" onClick={() => handleSubmit(4)}>
                            Save & Continue
                        </Button>
                    )}
                </div>
                <div className="flex justify-between md:justify-end mb-4 md:!mb-0">
                    {!isTablet && (
                        <Button onClick={() => navigationToDiffStepper(2)}>
                            Back to products
                        </Button>
                    )}
                    {isTablet && (
                        <Button className="w-full" onClick={() => handleSubmit(4)}>
                            Save & Continue
                        </Button>
                    )}
                    <div className="ml-[15px]">
                        <Button className="md:min-w-[150px]" onClick={() => navigationToDiffStepper(4)}>
                            Go to Summary
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProducts;
